import { useState } from 'react';
import type { ConversationItem } from '@vivport/backend/types';
import { MessageDraftSendDialog } from '@/components/message/MessageDraftSendDialog';
import { Button } from '@/components/ui/button';
import { useNavigateWithContentFlush } from '@/hooks';
import { useContentDetail } from '@/hooks/useContentQueries';
import { ExternalLink, Send } from 'lucide-react';

type DraftSendReadyItem = Extract<ConversationItem, { kind: 'draft_send_ready' }>;

export interface ProvenanceDraftSendReadyCardProps {
  item: DraftSendReadyItem;
}

export function ProvenanceDraftSendReadyCard({ item }: ProvenanceDraftSendReadyCardProps) {
  const navigate = useNavigateWithContentFlush();
  const [dialogOpen, setDialogOpen] = useState(false);
  const detailQuery = useContentDetail(item.draftContentId);
  const detail = detailQuery.data;
  const sent =
    detail !== undefined && detail.contentType === 'MESSAGE'
      ? detail.isDraft === false || detail.sentAt !== null
      : false;

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <Send className="h-4 w-4 shrink-0" />
        <p className="font-medium">Ready to send</p>
        <button
          type="button"
          onClick={() => navigate(item.url)}
          className="ml-auto rounded p-1 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
          aria-label="Open draft"
          title="Open draft"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
      <p className="mt-1 min-w-0 truncate font-medium">{item.subject ?? '(no subject)'}</p>
      <p className="min-w-0 truncate text-xs text-muted-foreground">To {item.toSummary}</p>
      <p className="min-w-0 truncate text-xs text-muted-foreground">From {item.sendingAccountEmail ?? '—'}</p>
      {item.authoredPreview ? (
        <p className="mt-1 min-w-0 truncate text-xs text-muted-foreground">{item.authoredPreview}</p>
      ) : null}
      <div className="mt-2">
        {sent ? (
          <p className="text-xs font-medium text-muted-foreground">Sent</p>
        ) : (
          <Button type="button" size="sm" onClick={() => setDialogOpen(true)}>
            <Send className="mr-2 h-3.5 w-3.5" />
            Send
          </Button>
        )}
      </div>
      <MessageDraftSendDialog open={dialogOpen} item={item} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
